import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../store/auth.store';

const DashboardLayout: React.FC = () => {
  const { user, userRole, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = userRole === 'admin' 
    ? [
        { label: 'Dashboard', path: '/admin/dashboard', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /> },
        { label: 'User Control', path: '/admin/users', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /> },
        { label: 'KYC Vault', path: '/admin/kyc', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /> },
        { label: 'Withdrawals', path: '/admin/withdrawals', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /> },
        { label: 'Tradings', path: '/admin/trading', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1 1V4z" /> },
        { label: 'BTC Config', path: '/admin/btc-address', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /> },
      ]
    : [
        { label: 'Dashboard', path: '/dashboard', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /> },
        { label: 'Trade History', path: '/tradings', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /> },
        { label: 'Withdraw', path: '/withdrawals', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /> },
        { label: 'My Profile', path: '/profile', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /> },
      ];

  const isAgaMember = user?.aga === 1;

  // Split nav items for mobile logo placement
  const mobileNavItems = navItems.slice(0, 4);
  const leftNav = mobileNavItems.slice(0, 2);
  const rightNav = mobileNavItems.slice(2, 4);

  return (
    <div className="flex h-screen bg-slate-950 overflow-hidden font-sans relative">
      
      {/* GLOBAL CIRCUIT BACKGROUND */}
      <div className="absolute inset-0 pointer-events-none opacity-20 z-0">
        <svg className="w-full h-full" viewBox="0 0 1000 1000" preserveAspectRatio="xMidYMid slice">
          <path d="M0,100 L200,100 L250,150 L250,300 L300,350 L500,350" stroke="rgba(139,92,246,0.1)" strokeWidth="1" fill="none" className="circuit-line" />
          <path d="M1000,800 L800,800 L750,750 L750,600 L700,550 L500,550" stroke="rgba(139,92,246,0.1)" strokeWidth="1" fill="none" className="circuit-line" />
          <circle cx="200" cy="100" r="2" fill="#8b5cf6" className="data-packet" />
          <circle cx="800" cy="800" r="2" fill="#8b5cf6" className="data-packet" />
        </svg>
      </div>

      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-72 bg-slate-950 border-r border-white/5 p-6 z-10 relative">
        <div className="flex items-center space-x-3 px-2 mb-10">
          <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center shadow-2xl shadow-purple-600/40 relative">
            <div className="absolute inset-0 bg-white/20 rounded-xl animate-pulse"></div>
            {/* Sidebar Hex Logo */}
            <svg className="w-6 h-6 text-white relative z-10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L3 7V17L12 22L21 17V7L12 2Z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round"/>
              <path d="M12 6L7 15H9.5L12 10L14.5 15H17L12 6Z" fill="currentColor"/>
              <path d="M8 18L12 16L16 18" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-extrabold tracking-tight text-white leading-none">AICW</h1>
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{userRole === 'admin' ? 'Admin Ops' : 'Main Menu'}</span>
          </div>
        </div>
        
        <nav className="flex-1 space-y-1 custom-scrollbar overflow-y-auto pr-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center px-4 py-3.5 rounded-2xl transition-all duration-300 group ${
                  isActive 
                    ? 'bg-purple-600/10 text-purple-400 border border-purple-500/20 shadow-inner' 
                    : 'text-slate-400 hover:text-white hover:bg-slate-900'
                }`}
              >
                <svg className={`w-5 h-5 mr-3 transition-colors ${isActive ? 'text-purple-400' : 'text-slate-500 group-hover:text-slate-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {item.icon}
                </svg>
                <span className="font-bold text-sm tracking-tight">{item.label}</span>
                {isActive && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-purple-500 shadow-[0_0_8px_rgba(139,92,246,0.8)]"></div>}
              </Link>
            );
          })}
        </nav>

        <div className="pt-6 border-t border-white/5">
          <button 
            onClick={handleLogout}
            className="flex items-center w-full px-4 py-3.5 text-slate-500 hover:text-red-400 rounded-2xl transition-all hover:bg-red-500/5 group"
          >
            <svg className="w-5 h-5 mr-3 group-hover:rotate-12 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span className="font-bold text-xs uppercase tracking-widest">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 bg-transparent z-10 relative">
        <header className="h-20 flex items-center justify-between px-6 lg:px-10 border-b border-white/5 bg-slate-950/80 backdrop-blur-xl">
           <div className="flex items-center space-x-4">
             {/* Mobile Logo Visibility */}
             <div className="lg:hidden w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center shadow-lg border border-white/10 shrink-0">
               <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L3 7V17L12 22L21 17V7L12 2Z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round"/>
                  <path d="M12 6L7 15H9.5L12 10L14.5 15H17L12 6Z" fill="currentColor"/>
                  <path d="M8 18L12 16L16 18" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
               </svg>
             </div>
             <div>
               <h2 className="text-xl font-black text-white uppercase tracking-tight">
                  {navItems.find(i => i.path === location.pathname)?.label || 'Overview'}
               </h2>
               <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest leading-none">Online & Secure</span>
             </div>
           </div>
           
           <div className="flex items-center space-x-4">
              {isAgaMember && (
                <div className="hidden sm:flex items-center px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                  <span className="text-[9px] font-black text-amber-500 uppercase tracking-widest">AGA ELITE</span>
                </div>
              )}
              <div className="hidden sm:flex flex-col text-right">
                <p className="text-sm font-bold text-white leading-none">{user?.fname} {user?.lname}</p>
                <p className="text-[9px] text-purple-500 font-black uppercase tracking-widest mt-1">{userRole}</p>
              </div>
              <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-black shadow-lg border border-white/10 group cursor-pointer hover:scale-105 transition-transform relative">
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 border-2 border-slate-950 rounded-full"></div>
                {user?.fname?.[0] || 'U'}
              </div>
           </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6 lg:p-10 pb-32 custom-scrollbar">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile Nav with Centered Logo */}
      <nav className="lg:hidden fixed bottom-6 left-6 right-6 h-20 bg-slate-900/80 backdrop-blur-2xl border border-white/10 rounded-[2.5rem] flex items-center px-4 z-50 shadow-2xl">
        <div className="flex-1 flex justify-around">
          {leftNav.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link 
                key={item.path} 
                to={item.path} 
                className={`flex flex-col items-center space-y-1 transition-all duration-300 ${isActive ? 'text-purple-400 scale-110' : 'text-slate-500'}`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {item.icon}
                </svg>
                <span className="text-[8px] font-black uppercase tracking-tighter">{item.label}</span>
              </Link>
            );
          })}
        </div>
        
        {/* Centered Hex-Logo */}
        <div className="relative -top-5">
           <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-[1.25rem] flex items-center justify-center shadow-[0_10px_30px_rgba(124,58,237,0.4)] border border-white/20 animate-in zoom-in duration-500">
              <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L3 7V17L12 22L21 17V7L12 2Z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round"/>
                <path d="M12 6L7 15H9.5L12 10L14.5 15H17L12 6Z" fill="currentColor"/>
              </svg>
           </div>
        </div>

        <div className="flex-1 flex justify-around">
          {rightNav.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link 
                key={item.path} 
                to={item.path} 
                className={`flex flex-col items-center space-y-1 transition-all duration-300 ${isActive ? 'text-purple-400 scale-110' : 'text-slate-500'}`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {item.icon}
                </svg>
                <span className="text-[8px] font-black uppercase tracking-tighter">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default DashboardLayout;